/**
 * Shared pieces of the Client → Billing commercial workspace.
 *
 * Kept separate so the four screens read as one coherent workspace
 * rather than four unrelated tables: the same readiness banner, the
 * same line editor, the same vocabulary.
 *
 * VALIDATE and ACTIVATE are deliberately distinct actions with distinct
 * wording, because they mean different things:
 *   ACTIVATE  = "this specification is now the authoritative baseline"
 *   VALIDATE  = "an authorized operator has reviewed these commercial
 *                terms and considers them accurate"
 * Collapsing them into one click would let unreviewed terms become
 * renewal-actionable.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle, Warning, SealCheck } from "@phosphor-icons/react";
import { money } from "./BillingSpecificationPanels";

export const CATEGORIES = [
  { value: "RECURRING_LICENSE", label: "Recurring licence", recurring: true },
  { value: "RECURRING_MODULE", label: "Recurring module", recurring: true },
  { value: "RECURRING_INTEGRATION", label: "Recurring integration", recurring: true },
  { value: "RECURRING_SERVICE", label: "Recurring service", recurring: true },
  { value: "ONE_TIME_SERVICE", label: "One-time service", recurring: false },
  { value: "DISCOUNT_OR_CREDIT", label: "Credit / discount", recurring: null },
  { value: "OTHER", label: "Other", recurring: null },
];

export const PERIODS = [
  { value: "one_time", label: "One time" },
  { value: "month", label: "Per month" },
  { value: "year", label: "Per year" },
];

/* ── Renewal readiness ───────────────────────────────────────────── */

/**
 * RENEWAL READY / RENEWAL HELD with specific, actionable reasons.
 * A client is never silently skipped — if they are held, this says why.
 */
export function RenewalReadinessBanner({ clientId, compact }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/renewals/clients/${clientId}/readiness`);
      setData(r.data);
    } catch {
      setData(null);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const ready = data.renewal_ready;

  return (
    <div
      className={`border px-4 py-3 ${ready
        ? "border-[#BBF7D0] bg-[#F0FDF4]"
        : "border-[#FDE68A] bg-[#FFFBEB]"}`}
      data-testid="renewal-readiness"
    >
      <div className="flex items-start gap-2">
        {ready
          ? <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-[#166534]" aria-hidden="true" />
          : <Warning size={16} weight="fill" className="mt-0.5 shrink-0 text-[#92400E]" aria-hidden="true" />}
        <div className="min-w-0">
          <p className={`text-xs font-black uppercase tracking-wider ${ready ? "text-[#166534]" : "text-[#92400E]"}`}>
            {ready ? "Renewal ready" : "Renewal held"}
          </p>
          {!ready && (
            <ul className="mt-1 space-y-0.5 text-xs text-[#525252] list-disc list-inside">
              {data.reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
          {ready && !compact && (
            <p className="text-xs text-[#525252] mt-0.5">
              Current annual recurring value{" "}
              <strong className="font-mono">
                {money(data.current_annual_recurring_value, null)}
              </strong>
              {data.next_renewal_date && <> · next renewal {data.next_renewal_date}</>}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The commercial status bar for the Current Recurring Specification —
 * readiness and the validate action together.
 *
 * CONTROLLED on purpose. This bar used to fetch and hold its own copy of
 * readiness and refresh it after validating the baseline, while the
 * owning panel held a second copy it only ever loaded once. Validating
 * refreshed this bar and nothing else, so the screen contradicted
 * itself: RENEWAL READY here, "Renewal held — billing baseline has not
 * been validated" on the Generate Proposal card beside it, until the
 * operator happened to reload. One readiness object now reaches every
 * surface that reads it, and `onValidated` reloads the owner so they
 * move together.
 */
export function CommercialStatusBar({ clientId, isAdmin, readiness, onValidated }) {
  if (!readiness) return null;
  const data = readiness;
  const ready = data.renewal_ready;

  return (
    <div className={`border ${ready ? "border-[#BBF7D0] bg-[#F0FDF4]" : "border-[#FDE68A] bg-[#FFFBEB]"}`}
         data-testid="commercial-status-bar">
      <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          {ready
            ? <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-[#166534]" aria-hidden="true" />
            : <Warning size={16} weight="fill" className="mt-0.5 shrink-0 text-[#92400E]" aria-hidden="true" />}
          <div className="min-w-0">
            <p className={`text-xs font-black uppercase tracking-wider ${ready ? "text-[#166534]" : "text-[#92400E]"}`}>
              {ready ? "Renewal ready" : "Renewal held"}
            </p>
            {!ready ? (
              <ul className="mt-1 space-y-0.5 text-xs text-[#525252] list-disc list-inside">
                {data.reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-[#525252] mt-0.5">
                Current annual recurring value{" "}
                <strong className="font-mono">
                  {money(data.current_annual_recurring_value, null)}
                </strong>
                {data.next_renewal_date && <> · next renewal {data.next_renewal_date}</>}
              </p>
            )}
          </div>
        </div>
        {isAdmin && data.active_specification_id && (
          <ValidateBaselineButton
            clientId={clientId}
            validatedAt={data.baseline_validated_at}
            validatedBy={data.baseline_validated_by}
            onDone={onValidated}
          />
        )}
      </div>
    </div>
  );
}

/* ── Validate baseline (distinct from Activate) ──────────────────── */

export function ValidateBaselineButton({ clientId, validatedAt, validatedBy, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/renewals/clients/${clientId}/validate-baseline`, null,
        { params: { note: note || undefined } });
      toast.success("Commercial baseline validated.");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not validate the baseline");
    }
    setBusy(false);
  };

  if (validatedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[#166534]"
            data-testid="baseline-validated">
        <SealCheck size={14} weight="fill" aria-hidden="true" />
        Baseline validated {String(validatedAt).slice(0, 10)}
        {validatedBy && <span className="text-[#737373]">by {validatedBy}</span>}
      </span>
    );
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}
              className="bg-white border border-[#166534] text-[#166534] hover:bg-[#F0FDF4] rounded-none h-8 text-[11px] uppercase"
              data-testid="validate-baseline">
        Validate commercial baseline
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Validate this client&rsquo;s commercial baseline?</DialogTitle>
            <DialogDescription className="text-xs">
              Validating records that <strong>Eyvo has reviewed these commercial terms
              and considers them authoritative</strong> for preparing future renewals.
              It is separate from activation: activation says which specification is
              current, validation says the figures in it have been checked.
            </DialogDescription>
          </DialogHeader>
          <p className="text-[11px] text-[#737373] leading-relaxed">
            Until this is done, renewals for this client stay held. If the commercial
            terms change afterwards, the validation is cleared automatically and will
            need confirming again.
          </p>
          <div>
            <label htmlFor="validation-note"
                   className="block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
              Note (optional) — what did you check against?
            </label>
            <input id="validation-note" value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g. checked against signed MSA and current invoice"
                   className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" />
          </div>
          <DialogFooter>
            <Button onClick={() => setOpen(false)}
                    className="bg-white border border-[#E5E5E5] rounded-none">Cancel</Button>
            <Button onClick={submit} disabled={busy}
                    className="bg-[#166534] hover:bg-[#14532D] text-white rounded-none"
                    data-testid="validate-baseline-confirm">
              {busy ? "Validating…" : "Confirm validation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ── Specification line editor ───────────────────────────────────── */

const blankLine = () => ({
  description: "",
  commercial_category: "RECURRING_LICENSE",
  billing_period: "year",
  billing_quantity: 1,
  billing_unit: "Years",
  licensed_user_quantity: "",
  unit_price_period: 0,
  discount_pct: 0,
  include_in_renewal: true,
});

/**
 * Add or edit one commercial line. The category drives which other
 * fields matter, and a credit line REQUIRES an explicit recurring /
 * one-time answer — that single choice changes the renewal basis, so it
 * is never defaulted.
 */
export function SpecLineDialog({ open, onOpenChange, specId, line, onSaved }) {
  const [form, setForm] = useState(blankLine());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(line ? {
      description: line.description || "",
      commercial_category: line.commercial_category || "OTHER",
      billing_period: line.billing_period || "year",
      billing_quantity: Number(line.billing_quantity ?? 1),
      billing_unit: line.billing_unit || "",
      licensed_user_quantity: line.licensed_user_quantity ?? "",
      unit_price_period: Number(line.unit_price_period ?? 0),
      discount_pct: Number(line.discount_pct ?? 0),
      include_in_renewal: !!line.include_in_renewal,
    } : blankLine());
  }, [open, line]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cat = CATEGORIES.find((c) => c.value === form.commercial_category);
  const isCredit = form.commercial_category === "DISCOUNT_OR_CREDIT";
  const isOneTimeService = form.commercial_category === "ONE_TIME_SERVICE";
  const isLicence = form.commercial_category === "RECURRING_LICENSE";

  // Keep the fields coherent so the API never has to reject the payload.
  const onCategory = (value) => {
    const meta = CATEGORIES.find((c) => c.value === value);
    setForm((f) => ({
      ...f,
      commercial_category: value,
      billing_period: value === "ONE_TIME_SERVICE" ? "one_time"
        : (meta?.recurring && f.billing_period === "one_time" ? "year" : f.billing_period),
      include_in_renewal: value === "ONE_TIME_SERVICE" ? false
        : (meta?.recurring ? true : f.include_in_renewal),
    }));
  };

  const onRecurrence = (recurs) => {
    setForm((f) => ({
      ...f,
      include_in_renewal: recurs,
      billing_period: recurs ? (f.billing_period === "one_time" ? "year" : f.billing_period)
        : "one_time",
    }));
  };

  const save = async () => {
    if (!form.description.trim()) { toast.error("A description is required."); return; }
    if (Number(form.billing_quantity) === 0) { toast.error("Quantity cannot be zero."); return; }
    if (Number(form.billing_quantity) < 0 && Number(form.unit_price_period) < 0) {
      toast.error("Use a negative quantity or a negative unit price for a credit, but not both.");
      return;
    }
    const payload = {
      ...form,
      billing_quantity: Number(form.billing_quantity),
      unit_price_period: Number(form.unit_price_period),
      discount_pct: Number(form.discount_pct),
      licensed_user_quantity: form.licensed_user_quantity === ""
        ? null : Number(form.licensed_user_quantity),
      billing_unit: form.billing_unit || null,
    };
    setBusy(true);
    try {
      if (line?.id) await api.patch(`/billing-spec/specs/${specId}/lines/${line.id}`, payload);
      else await api.post(`/billing-spec/specs/${specId}/lines`, payload);
      toast.success(line?.id ? "Line updated" : "Line added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the line");
    }
    setBusy(false);
  };

  const field = "w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]";
  const lbl = "block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle>{line?.id ? "Edit commercial line" : "Add commercial line"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label htmlFor="sl-desc" className={lbl}>Description</label>
            <input id="sl-desc" value={form.description}
                   onChange={(e) => set("description", e.target.value)}
                   className={field} data-testid="spec-line-description" />
          </div>

          <div>
            <label htmlFor="sl-cat" className={lbl}>What is this line?</label>
            <select id="sl-cat" value={form.commercial_category}
                    onChange={(e) => onCategory(e.target.value)}
                    className={field} data-testid="spec-line-category">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="sl-period" className={lbl}>Billing frequency</label>
            <select id="sl-period" value={form.billing_period}
                    onChange={(e) => set("billing_period", e.target.value)}
                    disabled={isOneTimeService}
                    className={`${field} disabled:bg-[#FAFAFA] disabled:text-[#A3A3A3]`}
                    data-testid="spec-line-period">
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="sl-qty" className={lbl}>
              Billing quantity {form.billing_period === "month" && <span className="normal-case text-[#737373]">(e.g. 12 months)</span>}
            </label>
            <input id="sl-qty" type="number" step="0.5" value={form.billing_quantity}
                   onChange={(e) => set("billing_quantity", e.target.value)}
                   className={`${field} font-mono text-right`} data-testid="spec-line-qty" />
          </div>

          <div>
            <label htmlFor="sl-price" className={lbl}>Unit price (per period)</label>
            <input id="sl-price" type="number" step="0.01" value={form.unit_price_period}
                   onChange={(e) => set("unit_price_period", e.target.value)}
                   title="Enter a negative amount for a credit, rebate or discount."
                   className={`${field} font-mono text-right`} data-testid="spec-line-price" />
          </div>

          <div>
            <label htmlFor="sl-users" className={lbl}>
              Licensed users {isLicence && <span className="text-[#DC2626]">— required</span>}
            </label>
            <input id="sl-users" type="number" step="1" value={form.licensed_user_quantity}
                   onChange={(e) => set("licensed_user_quantity", e.target.value)}
                   placeholder="e.g. 25"
                   className={`${field} font-mono text-right`} data-testid="spec-line-users" />
            <p className="text-[10px] text-[#737373] mt-1 leading-snug">
              Separate from billing quantity — 25 users billed as 12 months means
              quantity 12, users 25. The user count never multiplies the price.
            </p>
          </div>

          <div>
            <label htmlFor="sl-disc" className={lbl}>Line discount %</label>
            <input id="sl-disc" type="number" step="0.01" min="0" max="100"
                   value={form.discount_pct}
                   onChange={(e) => set("discount_pct", e.target.value)}
                   className={`${field} font-mono text-right`} />
          </div>

          <div className="md:col-span-2">
            {isCredit ? (
              <fieldset className="border border-[#FDE68A] bg-[#FFFBEB] p-3">
                <legend className="text-[10px] font-bold uppercase tracking-wider text-[#92400E] px-1">
                  Does this credit recur? — required
                </legend>
                <p className="text-[11px] text-[#525252] mb-2 leading-snug">
                  A recurring credit carries into every future renewal. A one-time credit
                  applies now and never again. This choice changes the renewal basis, so
                  it is never assumed.
                </p>
                <div className="flex gap-4 text-xs">
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" name="credit-recurs" checked={form.include_in_renewal === true}
                           onChange={() => onRecurrence(true)} data-testid="credit-recurring" />
                    Recurring — carries into renewals
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" name="credit-recurs" checked={form.include_in_renewal === false}
                           onChange={() => onRecurrence(false)} data-testid="credit-one-time" />
                    One-time only
                  </label>
                </div>
              </fieldset>
            ) : (
              <label className="inline-flex items-center gap-2 text-xs text-[#525252]">
                <input type="checkbox" checked={!!form.include_in_renewal}
                       disabled={isOneTimeService}
                       onChange={(e) => set("include_in_renewal", e.target.checked)}
                       data-testid="spec-line-include" />
                Carries into future renewals
                {isOneTimeService && (
                  <span className="text-[#737373]">— one-time services never renew</span>
                )}
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}
                  className="bg-white border border-[#E5E5E5] rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy}
                  className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none"
                  data-testid="spec-line-save">
            {busy ? "Saving…" : (line?.id ? "Save line" : "Add line")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Amendment creation ──────────────────────────────────────────── */

const AMENDMENT_ACTIONS = [
  { value: "change_licensed_users", label: "Change licensed users" },
  { value: "change_quantity", label: "Change quantity" },
  { value: "add_line", label: "Add a module / integration / service" },
  { value: "remove_line", label: "Remove a module / integration / service" },
  { value: "change_price", label: "Change a price" },
  { value: "change_discount", label: "Change a credit / discount" },
  { value: "add_credit", label: "Add a recurring credit" },
  { value: "remove_credit", label: "Remove a credit" },
  { value: "other", label: "Other commercial change" },
];

/**
 * Record a mid-term commercial change. Creating one changes nothing —
 * it must be approved, then applied. The Initial Specification is never
 * rewritten by any of this.
 */
export function AmendmentDialog({ open, onOpenChange, clientId, items, onSaved }) {
  const [form, setForm] = useState({
    action: "change_licensed_users",
    effective_date: new Date().toISOString().slice(0, 10),
    recurring_item_id: "",
    description: "",
    reason: "",
    delta_annual_value: 0,
    new_value_json: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.effective_date) { toast.error("An effective date is required."); return; }
    let newValue = null;
    if (form.new_value_json.trim()) {
      try {
        newValue = JSON.parse(form.new_value_json);
      } catch {
        toast.error("New values must be valid JSON, e.g. {\"licensed_user_quantity\": 35}");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/billing-spec/clients/${clientId}/amendments`, {
        action: form.action,
        effective_date: form.effective_date,
        recurring_item_id: form.recurring_item_id || null,
        description: form.description || null,
        reason: form.reason || null,
        delta_annual_value: Number(form.delta_annual_value) || 0,
        new_value: newValue,
      });
      toast.success("Change recorded — it needs approving, then applying.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record the change");
    }
    setBusy(false);
  };

  const field = "w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]";
  const lbl = "block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record a commercial change</DialogTitle>
          <DialogDescription className="text-xs">
            Recording a change does not alter anything yet. It has to be approved and
            then applied, and the original specification is never rewritten.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label htmlFor="am-action" className={lbl}>What changed?</label>
            <select id="am-action" value={form.action}
                    onChange={(e) => set("action", e.target.value)}
                    className={field} data-testid="amendment-action">
              {AMENDMENT_ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="am-date" className={lbl}>Effective date</label>
            <input id="am-date" type="date" value={form.effective_date}
                   onChange={(e) => set("effective_date", e.target.value)}
                   className={field} data-testid="amendment-date" />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="am-item" className={lbl}>Which line does this affect?</label>
            <select id="am-item" value={form.recurring_item_id}
                    onChange={(e) => set("recurring_item_id", e.target.value)}
                    className={field} data-testid="amendment-item">
              <option value="">(not tied to a specific line)</option>
              {(items || []).map((i) => (
                <option key={i.id} value={i.id}>{i.description}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="am-desc" className={lbl}>Description</label>
            <input id="am-desc" value={form.description}
                   onChange={(e) => set("description", e.target.value)}
                   placeholder="e.g. increased from 25 to 35 licensed users"
                   className={field} />
          </div>

          <div>
            <label htmlFor="am-delta" className={lbl}>Annual impact</label>
            <input id="am-delta" type="number" step="0.01" value={form.delta_annual_value}
                   onChange={(e) => set("delta_annual_value", e.target.value)}
                   className={`${field} font-mono text-right`}
                   title="Negative for a reduction." />
          </div>
          <div>
            <label htmlFor="am-reason" className={lbl}>Reason</label>
            <input id="am-reason" value={form.reason}
                   onChange={(e) => set("reason", e.target.value)}
                   className={field} />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="am-json" className={lbl}>
              New values to apply (optional, JSON)
            </label>
            <input id="am-json" value={form.new_value_json}
                   onChange={(e) => set("new_value_json", e.target.value)}
                   placeholder='{"licensed_user_quantity": 35}'
                   className={`${field} font-mono text-xs`} data-testid="amendment-json" />
            <p className="text-[10px] text-[#737373] mt-1 leading-snug">
              Applied to the affected line when the change is applied. Supported fields:
              quantity, unit_price_annual, discount_pct, licensed_user_quantity,
              include_in_renewal, commercial_category, billing_period, description.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}
                  className="bg-white border border-[#E5E5E5] rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy}
                  className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none"
                  data-testid="amendment-save">
            {busy ? "Recording…" : "Record change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
